import { Serializer } from "@shared/mirage/helpers/Serializer";
import { memo, useCallback, useEffect } from "react";
import EnforcementAppealsSearchState from "./EnforcementAppealsSearchState";
import EnforcementAppealsSimpleSearch from "./EnforcementAppealsSimpleSearch";
import EnforcementAppealsService from "@publicPortal/services/EnforcementAppealsService";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import { useEnforcementAppealsSearchStore } from "@publicPortal/store/store";
import { EnforcementAppealsFilter } from "@shared/publicportal/contracts/EnforcementAppealsFilter";
import SearchTypeSwitchingTabs from "@publicPortal/components/advancedSearch/SearchTypeSwitchingTabs";
import SearchActions from "../SearchActions";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { SearchType } from "@shared/publicportal/contracts/Search";
import EnforcementAppealsAdvancedSearch from "@publicPortal/components/advancedSearch/EnforcementAppealsAdvancedSearch";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { PublicPortalMapSearch } from "@publicPortal/components/mapSearch/PublicPortalMapSearch";

const service = new EnforcementAppealsService();
const pp = new PublicPortalRoutingService();

const EnforcementAppealsSearch = memo(() => {
    const state = useEnforcementAppealsSearchStore((s) => s.enforcementAppealsSearch);
    const updateEnforcementAppealsSearch = useEnforcementAppealsSearchStore((s) => s.updateEnforcementAppealsSearch);
    const updateEnforcementAppealsFilter = useEnforcementAppealsSearchStore((s) => s.updateEnforcementAppealsFilter);
    const resetEnforcementAppealsFilter = useEnforcementAppealsSearchStore((s) => s.resetEnforcementAppealsFilter);
    const navigate = useStableNavigate();

    const setStateModifier = useCallback(
        (modifier: Partial<EnforcementAppealsFilter>) => {
            updateEnforcementAppealsFilter(modifier);
        },
        [updateEnforcementAppealsFilter]
    );
    const onSearchTypeChanged = useCallback(
        (newSearchType: SearchType) => {
            updateEnforcementAppealsSearch({ searchType: newSearchType });
        },
        [updateEnforcementAppealsSearch]
    );

    useEffect(() => {
        const getFilteringLists = async () => {
            const filter = await service.getFilteringLists();
            updateEnforcementAppealsSearch({ decisionTypes: filter?.decisionTypes || [], parishes: filter?.parishes || [], wards: filter?.wards || [] });
        };
        getFilteringLists();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prepareFilter = useCallback(() => {
        switch (state.searchType) {
            case "Simple": {
                return {
                    searchType: state.searchType,
                    appealRef: state.filter.appealRef,
                    noticeRef: state.filter.noticeRef,
                    inspectorateRef: state.filter.inspectorateRef,
                };
            }
            case "Advanced": {
                return { ...state.filter, searchType: state.searchType };
            }
            case "Map": {
                return { searchType: state.searchType, geometry: state.filter.geometry };
            }
            default:
                return null;
        }
    }, [state.filter, state.searchType]);
    const onClear = useCallback(() => {
        resetEnforcementAppealsFilter();
    }, [resetEnforcementAppealsFilter]);
    const onSubmit = useCallback(() => {
        const filter = prepareFilter();
        const searchParams = Serializer.toQueryParamsNotEmptyOnly(filter);
        EntityTableStateStorage.clear("enforcementappeals");
        navigate(`${pp.SearchRoute("EnforcementAppeals", searchParams)}`);
    }, [navigate, prepareFilter]);

    return (
        <>
            <SearchTypeSwitchingTabs searchType={state.searchType} onSearchTypeChanged={onSearchTypeChanged} />
            {getForm()}
            <SearchActions onCancelHandler={onClear} onSubmitHandler={onSubmit} />
        </>
    );

    function getForm() {
        switch (state.searchType) {
            case "Simple": {
                return <EnforcementAppealsSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <EnforcementAppealsAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return <PublicPortalMapSearch setStateModifier={setStateModifier} geometry={state.filter.geometry} />;
            }
            default:
                return null;
        }
    }
});

export default EnforcementAppealsSearch;
